/**
 * 背景辅助工具类
 * 统一管理背景创建，减少重复代码
 */
export default class BackgroundHelper {
    /**
     * 创建半透明黑色背景
     * @param {Phaser.Scene} scene - 场景对象
     * @param {number} alpha - 透明度（0-1，默认0.8）
     * @param {number} color - 颜色值（默认0x000000黑色）
     * @returns {Phaser.GameObjects.Graphics} 背景图形对象
     */
    static createSemiTransparentBackground(scene, alpha = 0.8, color = 0x000000) {
        const bg = scene.add.graphics();
        bg.fillStyle(color, alpha);
        bg.fillRect(0, 0, scene.cameras.main.width, scene.cameras.main.height);
        bg.setScrollFactor(0);
        bg.setDepth(-1);
        return bg;
    }
    
    /**
     * 创建渐变背景
     * @param {Phaser.Scene} scene - 场景对象
     * @param {Object} options - 选项
     * @param {number} options.startColor - 起始颜色（RGB对象 {r, g, b}）
     * @param {number} options.endColor - 结束颜色（RGB对象 {r, g, b}）
     * @param {string} options.direction - 渐变方向 'vertical' | 'horizontal'（默认'vertical'）
     * @returns {Phaser.GameObjects.Graphics} 背景图形对象
     */
    static createGradientBackground(scene, options = {}) {
        const {
            startColor = { r: 66, g: 0, b: 0 },
            endColor = { r: 33, g: 0, b: 0 },
            direction = 'vertical'
        } = options;
        
        const bg = scene.add.graphics();
        bg.setDepth(-1);
        
        const width = scene.cameras.main.width;
        const height = scene.cameras.main.height;
        
        if (direction === 'vertical') {
            // 垂直渐变
            for (let y = 0; y < height; y++) {
                const ratio = y / height;
                const r = Math.floor(Phaser.Math.Linear(startColor.r, endColor.r, ratio));
                const g = Math.floor(Phaser.Math.Linear(startColor.g, endColor.g, ratio));
                const b = Math.floor(Phaser.Math.Linear(startColor.b, endColor.b, ratio));
                bg.fillStyle(Phaser.Display.Color.GetColor(r, g, b), 1);
                bg.fillRect(0, y, width, 1);
            }
        } else {
            // 水平渐变
            for (let x = 0; x < width; x++) {
                const ratio = x / width;
                const r = Math.floor(Phaser.Math.Linear(startColor.r, endColor.r, ratio));
                const g = Math.floor(Phaser.Math.Linear(startColor.g, endColor.g, ratio));
                const b = Math.floor(Phaser.Math.Linear(startColor.b, endColor.b, ratio));
                bg.fillStyle(Phaser.Display.Color.GetColor(r, g, b), 1);
                bg.fillRect(x, 0, 1, height);
            }
        }
        
        return bg;
    }
    
    /**
     * 创建纯色背景
     * @param {Phaser.Scene} scene - 场景对象
     * @param {number} color - 颜色值（默认0x1a1a2e）
     * @returns {void}
     */
    static createSolidBackground(scene, color = 0x1a1a2e) {
        scene.cameras.main.backgroundColor = Phaser.Display.Color.ValueToColor(color);
    }
    
    /**
     * 创建带边框的背景面板
     * @param {Phaser.Scene} scene - 场景对象
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {number} width - 宽度
     * @param {number} height - 高度
     * @param {Object} options - 选项
     * @param {number} options.fillColor - 填充颜色（默认0x000000）
     * @param {number} options.fillAlpha - 填充透明度（默认0.8）
     * @param {number} options.borderColor - 边框颜色（默认0x4cc9f0）
     * @param {number} options.borderWidth - 边框宽度（默认2）
     * @param {number} options.borderRadius - 圆角半径（默认0）
     * @returns {Phaser.GameObjects.Graphics} 背景图形对象
     */
    static createPanel(scene, x, y, width, height, options = {}) {
        const {
            fillColor = 0x000000,
            fillAlpha = 0.8,
            borderColor = 0x4cc9f0,
            borderWidth = 2,
            borderRadius = 0
        } = options;
        
        const panel = scene.add.graphics();
        
        // 填充
        if (borderRadius > 0) {
            panel.fillStyle(fillColor, fillAlpha);
            panel.fillRoundedRect(x, y, width, height, borderRadius);
        } else {
            panel.fillStyle(fillColor, fillAlpha);
            panel.fillRect(x, y, width, height);
        }
        
        // 边框
        if (borderWidth > 0) {
            panel.lineStyle(borderWidth, borderColor, 1);
            if (borderRadius > 0) {
                panel.strokeRoundedRect(x, y, width, height, borderRadius);
            } else {
                panel.strokeRect(x, y, width, height);
            }
        }
        
        panel.setScrollFactor(0);
        return panel;
    }
}

